import React from "react";
import { AlertTriangle, Flag, Lock } from "lucide-react";
import { DEP_TYPE, fmtDate, fmtDays, PLAN_VERSION_KIND, PLAN_VERSION_STATUS } from "../lib/labels";
import { Chip, ToneChip } from "./common";

export function DepBadge({ dep, testid }) {
  const conf = DEP_TYPE[dep.type] || { label: dep.type, desc: dep.type };
  return (
    <span className="ts-chip ts-chip-info" title={conf.desc} data-testid={testid}>
      {dep.from_wbs} {conf.label}{dep.lag ? `+${dep.lag}d` : ""}
    </span>
  );
}

export function Variance({ days, testid }) {
  if (days == null) return <span className="font-mono text-xs text-slate-400" data-testid={testid}>—</span>;
  const tone = days > 0 ? "text-red-600" : days < 0 ? "text-emerald-700" : "text-slate-500";
  return <span className={`font-mono text-xs font-semibold ${tone}`} data-testid={testid}>{fmtDays(days)}</span>;
}

export function VersionChip({ version, testid }) {
  if (!version) return <ToneChip label="Sin planning" tone="muted" testid={testid} />;
  return (
    <span className="inline-flex items-center gap-1" data-testid={testid}>
      <span className="font-mono text-xs text-slate-500">v{version.number}</span>
      <Chip map={PLAN_VERSION_KIND} value={version.kind} />
      <Chip map={PLAN_VERSION_STATUS} value={version.status} />
      {version.kind === "baseline" && ["aprobada", "vigente"].includes(version.status) && (
        <span className="inline-flex items-center gap-0.5 text-xs text-slate-400" title="Baseline aprobada: inmutable, nunca se sobrescribe">
          <Lock className="h-3 w-3" /> inmutable
        </span>
      )}
    </span>
  );
}

export function ValidationList({ validation, testid = "validation-list" }) {
  if (!validation) return null;
  const { errors = [], warnings = [] } = validation;
  if (!errors.length && !warnings.length) return <ToneChip label="Validación superada sin observaciones" tone="success" testid={`${testid}-ok`} />;
  return (
    <div className="space-y-2" data-testid={testid}>
      {errors.map((e, i) => (
        <div key={`e-${i}`} className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-2.5" data-testid={`${testid}-error-${i}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          <div>
            <p className="text-sm font-medium text-red-900">{e.message}</p>
            <p className="text-xs text-red-700">Error bloqueante · código {e.code} · corrige el dato de origen y vuelve a importar</p>
          </div>
        </div>
      ))}
      {warnings.map((w, i) => (
        <div key={`w-${i}`} className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 p-2.5" data-testid={`${testid}-warning-${i}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-900">{w.message}</p>
            <p className="text-xs text-amber-700">Advertencia · no bloquea la publicación, pero conviene revisarla</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DiffSummary({ diff, validation, testid = "diff-summary" }) {
  if (!diff) return null;
  const items = [
    ["nuevas", diff.added?.length ?? 0, "text-emerald-700"],
    ["modificadas", diff.modified?.length ?? 0, "text-amber-700"],
    ["eliminadas", diff.removed?.length ?? 0, "text-red-700"],
    ["warnings", validation?.warnings?.length ?? 0, "text-amber-700"],
    ["errores bloqueantes", validation?.errors?.length ?? 0, "text-red-700"],
  ];
  return (
    <div className="flex flex-wrap gap-3 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2" data-testid={testid}>
      {items.map(([label, n, tone]) => (
        <span key={label} className="text-xs" data-testid={`${testid}-${label.replace(/ /g, "-")}`}>
          <span className={`font-mono text-sm font-bold ${tone}`}>{n}</span> <span className="text-slate-500">{label}</span>
        </span>
      ))}
      {diff.against != null && <span className="ml-auto text-xs text-slate-400">comparado contra v{diff.against}</span>}
    </div>
  );
}

export function GanttChart({ activities, onSelect, selectedId }) {
  if (!activities?.length) return null;
  const ts = (s) => (s ? new Date(s).getTime() : null);
  const min = Math.min(...activities.map((a) => ts(a.baseline_start) || ts(a.current_start)).filter(Boolean));
  const max = Math.max(...activities.flatMap((a) => [ts(a.baseline_finish), ts(a.current_finish)]).filter(Boolean));
  const span = Math.max(1, max - min);
  const pos = (t) => Math.min(100, Math.max(0, ((t - min) / span) * 100));
  return (
    <div className="ts-card overflow-x-auto p-4" data-testid="gantt-chart">
      <div className="min-w-[760px]">
        <div className="mb-1 flex items-center gap-2 text-xs text-slate-400">
          <span className="w-56 shrink-0" />
          <span className="flex flex-1 justify-between font-mono"><span>{fmtDate(new Date(min).toISOString())}</span><span>{fmtDate(new Date(max).toISOString())}</span></span>
        </div>
        {activities.map((a) => {
          const bS = ts(a.baseline_start), bF = ts(a.baseline_finish);
          const cS = ts(a.current_start), cF = ts(a.current_finish);
          const delayed = cF && bF && cF > bF && (a.progress || 0) < 100;
          return (
            <button key={a.id} type="button" onClick={() => onSelect?.(a)} data-testid={`gantt-row-${a.id}`}
              className={`flex w-full items-center gap-2 border-b border-slate-50 py-0.5 text-left transition-colors hover:bg-blue-50/40 ${selectedId === a.id ? "bg-blue-50" : ""}`}>
              <span className="w-56 shrink-0 truncate pr-2 text-xs">
                <span className="mr-1 font-mono text-slate-400">{a.wbs}</span>
                <span className={a.critical ? "font-semibold text-slate-800" : "text-slate-600"}>{a.name}</span>
              </span>
              <span className="relative h-8 flex-1">
                {bS && bF && (
                  <span className="absolute top-0.5 h-2 rounded-sm bg-slate-200" style={{ left: `${pos(bS)}%`, width: `${Math.max(0.6, pos(bF) - pos(bS))}%` }}
                    title={`Baseline: ${fmtDate(a.baseline_start)} → ${fmtDate(a.baseline_finish)}`} />
                )}
                {cS && cF && (
                  <span className={`absolute top-3.5 h-3 overflow-hidden rounded-sm ${a.milestone ? "bg-amber-300" : delayed ? "bg-red-200" : "bg-blue-100"}`}
                    style={{ left: `${pos(cS)}%`, width: `${Math.max(0.6, pos(cF) - pos(cS))}%` }}
                    title={`Actual: ${fmtDate(a.current_start)} → ${fmtDate(a.current_finish)} · avance ${a.progress || 0}%`}>
                    <span className={`block h-full transition-[width] ${a.milestone ? "bg-amber-500" : delayed ? "bg-red-500" : "bg-blue-600"}`} style={{ width: `${a.progress || 0}%` }} />
                  </span>
                )}
                {a.milestone && cF && <Flag className="absolute top-3 h-3.5 w-3.5 text-amber-600" style={{ left: `calc(${pos(cF)}% + 3px)` }} />}
              </span>
            </button>
          );
        })}
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-sm bg-slate-200" /> Baseline</span>
          <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-sm bg-blue-600" /> Actual en plazo</span>
          <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-sm bg-red-500" /> Actual retrasada</span>
          <span className="flex items-center gap-1"><Flag className="h-3 w-3 text-amber-600" /> Hito</span>
        </div>
      </div>
    </div>
  );
}
